<template>
  <h3>{{ $t("user.recoverTitle") }}</h3>

  <form class="form" @submit.prevent="onSubmit()">
    <FormGroup :v="v$.email" label="label.email">
      <TextField
        v-model="formValue.email"
        type="text"
        inputmode="email"
        name="email"
        :disabled="props.saving"
        placeholder="placeholder.email"
        autocomplete="username"
      />
    </FormGroup>
    <FormGroup :v="v$.password" label="label.password">
      <PasswordField
        v-model="formValue.password"
        :disabled="props.saving"
        autocomplete="new-password"
      />
    </FormGroup>
    <FormGroup :v="v$.password_confirmation" label="label.repeatPassword">
      <PasswordField
        v-model="formValue.password_confirmation"
        :disabled="props.saving"
        autocomplete="new-password"
      />
    </FormGroup>
    <div class="form__actions">
      <AppButton type="submit" :loading="props.saving">
        {{ $t("user.recover") }}
      </AppButton>
    </div>
  </form>
</template>

<script setup lang="ts">
import AppButton from "@/components/app-button.vue";
import FormGroup from "@/components/form/form-group.vue";
import TextField from "@/components/form/text-field.vue";
import PasswordField from "@/components/form/password-field.vue";
import { computed, reactive } from "vue";
import { minLength, required, sameAs, email } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import { UserRecoverPasswordData } from "@/types";
import { getRecoverToken } from "./get-recover-token";

const props = defineProps<{
  saving?: boolean;
  errors?: Record<string, string[]>;
}>();

const emit = defineEmits<{
  (e: "submit", v: UserRecoverPasswordData): void;
}>();

const formValue = reactive<
  UserRecoverPasswordData & { password_confirmation: string }
>({
  password: "",
  password_confirmation: "",
  email: "",
  token: getRecoverToken()!,
});

const rules = computed(() => ({
  email: { required, email },
  password: { required, minLength: minLength(8) },
  password_confirmation: {
    required,
    equalPassword: sameAs(formValue.password),
  },
}));

const v$ = useVuelidate(rules, formValue, {
  $externalResults: computed(() => props.errors) as any,
});

const onSubmit = () => {
  v$.value.$touch();

  if (!v$.value.$invalid) {
    emit("submit", {
      token: formValue.token,
      password: formValue.password,
      password_confirmation: formValue.password,
      email: formValue.email,
    });
  }
};
</script>
